import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import Modal from '../shared/Modal';
import TestResultsView from './TestResultsView';
import DynamicFormBuilder from './DynamicFormBuilder';
import CustomInputConfig from './trigger-configs/CustomInputConfig';
import customAgentService from '../../services/customAgent';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import './TestCustomAgentModal.css';

const TestCustomAgentModal = ({ isOpen, onClose, customAgent, nodes = [], edges = [] }) => {
  const { user } = useAuth();
  const [step, setStep] = useState('input'); // 'input' or 'results'
  const [triggerData, setTriggerData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [sampleAccounts, setSampleAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // Find trigger node
  const triggerNode = nodes.find((node) => {
    const nodeType = node.data?.nodeType;
    return nodeType && nodeType.startsWith('trigger.');
  });

  const triggerType = triggerNode?.data?.nodeType || '';
  const triggerConfig = triggerNode?.data?.config || {};

  // Load accounts when modal opens
  useEffect(() => {
    if (isOpen && (triggerType === 'trigger.buyer_intent' || triggerType === 'trigger.deal_health')) {
      loadSampleAccounts();
    }
  }, [isOpen, triggerType]);

  const loadSampleAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await axios.get('/api/custom-agents/trigger-data/accounts', {
        params: { 
          limit: 100 // Load up to 100 accounts
        },
        withCredentials: true
      });
      setSampleAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading sample accounts:', error);
      setSampleAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleInputChange = (field, value) => {
    setTriggerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomInputChange = (config) => {
    setTriggerData(config.triggerData || {});
  };

  const handleAccountSelect = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedAccountIds(selectedOptions);
  };

  const getSelectedAccountNames = () => {
    return selectedAccountIds
      .map(id => {
        const account = sampleAccounts.find(acc => acc.id === id);
        return account ? (account.companyName || account.name || 'Unnamed Company') : null;
      })
      .filter(Boolean)
      .join('; ');
  };

  const buildTriggerData = () => {
    const baseData = {
      userId: customAgent?.userId || user?.id,
      triggeredAt: new Date().toISOString()
    };

    if (triggerType === 'trigger.buyer_intent') {
      return {
        ...baseData,
        triggerType: 'buyer_intent',
        accountId: selectedAccountIds.length === 1 ? selectedAccountIds[0] : selectedAccountIds,
        accountIds: selectedAccountIds,
        intentType: triggerConfig.intentType || 'engagement',
        conditions: triggerConfig.conditions || '',
        dealStageFilter: Array.isArray(triggerConfig.dealStageFilter) 
          ? triggerConfig.dealStageFilter 
          : (triggerConfig.dealStageFilter ? [triggerConfig.dealStageFilter] : ['all'])
      };
    }

    if (triggerType === 'trigger.deal_health') {
      return {
        ...baseData,
        triggerType: 'deal_health',
        accountId: selectedAccountIds.length === 1 ? selectedAccountIds[0] : selectedAccountIds,
        accountIds: selectedAccountIds,
        healthMetric: triggerConfig.healthMetric || 'overall',
        triggerCondition: triggerConfig.triggerCondition || '',
        dealStageFilter: Array.isArray(triggerConfig.dealStageFilter) 
          ? triggerConfig.dealStageFilter 
          : (triggerConfig.dealStageFilter ? [triggerConfig.dealStageFilter] : ['all'])
      };
    }

    if (triggerType === 'trigger.manual') {
      return {
        ...baseData,
        ...triggerData
      };
    }

    // Default: use provided triggerData
    return {
      ...baseData,
      ...triggerData
    };
  };

  const handleTest = async () => {
    if (!customAgent || !customAgent.id) {
      setError('CustomAgent not found');
      return;
    }

    // Validate required fields
    if ((triggerType === 'trigger.buyer_intent' || triggerType === 'trigger.deal_health') && selectedAccountIds.length === 0) {
      setError('Please select at least one account to test with');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const testTriggerData = buildTriggerData();
      
      // Start the test execution
      const response = await customAgentService.testCustomAgent(customAgent.id, testTriggerData);
      
      if (response.status === 'success' && response.data) {
        const executionId = response.data.executionId;
        
        // Immediately switch to results view with initial data
        setTestResults({
          ...response.data,
          execution: {
            id: executionId,
            status: 'running',
            nodeResults: {},
            executionPath: [],
            triggerData: testTriggerData
          }
        });
        setStep('results');
        setLoading(false); // Stop loading so we can show the results view
        
        // Poll for execution updates if we have an executionId
        if (executionId) {
          pollExecutionStatus(executionId);
        }
      } else {
        setError(response.message || 'Test execution failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error testing customAgent:', err);
      setError(err.response?.data?.message || err.message || 'Failed to test customAgent');
      setLoading(false);
    }
  };

  const pollExecutionStatus = async (executionId) => {
    const maxAttempts = 120; // Poll for up to 2 minutes (120 * 1 second)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const executionResponse = await customAgentService.getExecution(executionId);
        const execution = executionResponse.data || executionResponse;
        
        // Update results with latest execution data
        setTestResults(prev => ({
          ...prev,
          execution: execution
        }));
        
        // Continue polling if execution is still running
        if (execution.status === 'running' && attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000); // Poll every 1 second
        }
      } catch (err) {
        console.error('Error polling execution status:', err);
        // Stop polling on error
      }
    };
    
    // Start polling
    poll();
  };

  const handleClose = () => {
    setStep('input');
    setTriggerData({});
    setTestResults(null);
    setError('');
    setSelectedAccountIds([]);
    onClose();
  };

  const handleBackToInput = () => {
    setStep('input');
    setTestResults(null);
    setError('');
  };

  const renderInputForm = () => {
    if (!triggerNode) {
      return (
        <div className="test-modal-error">
          <p>No trigger node found in customAgent. Please add a trigger node to test the customAgent.</p>
        </div>
      );
    }

    // Buyer Intent Trigger
    if (triggerType === 'trigger.buyer_intent') {
      return (
        <div className="test-input-form">
          <div className="form-section">
            <h3>Test Buyer Intent Trigger</h3>
            <p className="form-description">
              Select sample accounts to test the customAgent. The customAgent will run with buyer intent data for these accounts.
            </p>
            
            <div className="config-field">
              <label>Select Accounts</label>
              {loadingAccounts ? (
                <div className="loading-indicator">Loading accounts...</div>
              ) : (
                <>
                  <select
                    multiple
                    value={selectedAccountIds}
                    onChange={handleAccountSelect}
                    className="account-select account-select-multi"
                    disabled={loadingAccounts}
                    size={8}
                  >
                    {sampleAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.companyName || account.name || 'Unnamed Company'}
                      </option>
                    ))}
                  </select>
                    <div className="selected-accounts-display">
                        <strong>Selected:</strong> {getSelectedAccountNames()}
                    </div>
                </>
              )}
              <small>Hold Ctrl/Cmd to select multiple accounts. Selected accounts will be shown below.</small>
            </div>

            <div className="config-field">
              <label>Intent Type</label>
              <div className="readonly-field">
                {triggerConfig.intentType || 'engagement'}
              </div>
              <small>From trigger configuration</small>
            </div>
          </div>
        </div>
      );
    }

    // Deal Health Trigger
    if (triggerType === 'trigger.deal_health') {
      return (
        <div className="test-input-form">
          <div className="form-section">
            <h3>Test Deal Health Trigger</h3>
            <p className="form-description">
              Select sample accounts to test the customAgent. The customAgent will run with deal health data for these accounts.
            </p>
            
            <div className="config-field">
              <label>Select Accounts</label>
              {loadingAccounts ? (
                <div className="loading-indicator">Loading accounts...</div>
              ) : (
                <>
                  <select
                    multiple
                    value={selectedAccountIds}
                    onChange={handleAccountSelect}
                    className="account-select account-select-multi"
                    disabled={loadingAccounts}
                    size={8}
                  >
                    {sampleAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.companyName || account.name || 'Unnamed Company'}
                      </option>
                    ))}
                  </select>
                    <div className="selected-accounts-display">
                      <strong>Selected:</strong> {getSelectedAccountNames()}
                    </div>
                </>
              )}
              <small>Hold Ctrl/Cmd to select multiple accounts. Selected accounts will be shown below.</small>
            </div>

            <div className="config-field">
              <label>Health Metric</label>
              <div className="readonly-field">
                {triggerConfig.healthMetric || 'overall'}
              </div>
              <small>From trigger configuration</small>
            </div>
          </div>
        </div>
      );
    }

    // Manual Trigger
    if (triggerType === 'trigger.manual') {
      const inputType = triggerConfig.inputType || 'new_opportunity';
      return (
        <div className="test-input-form">
          <div className="form-section">
            <h3>Test Manual Trigger</h3>
            <p className="form-description">
              Provide test data for the manual trigger. This will simulate the customAgent execution with your input.
            </p>
            
            <CustomInputConfig
              config={{
                inputType,
                formData: triggerData,
                triggerData
              }}
              onChange={handleCustomInputChange}
            />
          </div>
        </div>
      );
    }

    // Generic/Unknown Trigger
    return (
      <div className="test-input-form">
        <div className="form-section">
          <h3>Test CustomAgent</h3>
          <p className="form-description">
            Provide test trigger data as JSON:
          </p>
          
          <div className="config-field">
            <label>Trigger Data (JSON)</label>
            <textarea
              value={JSON.stringify(triggerData, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setTriggerData(parsed);
                  setError('');
                } catch (err) {
                  setError('Invalid JSON');
                }
              }}
              rows="10"
              className="json-input"
              placeholder='{"key": "value"}'
            />
            <small>Enter trigger data as JSON object</small>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'input' ? 'Test CustomAgent' : 'Test Results'}
      size="xlarge"
      closeOnOverlayClick={!loading}
    >
      <div className="test-customAgent-modal">
        {step === 'input' ? (
          <>
            {renderInputForm()}
            
            {error && (
              <div className="test-error-message">
                {error}
              </div>
            )}

            <div className="test-modal-actions">
              <button
                className="btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleTest}
                disabled={loading || !customAgent}
              >
                {loading ? 'Testing...' : 'Run Test'}
              </button>
            </div>
          </>
        ) : (
          <TestResultsView
            results={testResults}
            customAgent={customAgent}
            nodes={nodes}
            edges={edges}
            onBack={handleBackToInput}
            onClose={handleClose}
          />
        )}
      </div>
    </Modal>
  );
};

export default TestCustomAgentModal;
